// UI-NAV-1 (2026-05-07) — AppShell extracted from App.js inline rendering.
//
// Owns: impersonation banner, MFA-required banner, top nav, user-menu,
// sign-out, and the document frame around the routed page body.  App.js
// keeps the routing logic and passes the rendered page in as children.
//
// Why a shell: the UI Refinement Memo (§15.1) defines a global frame with
// persistent navigation, reserved locations for global search /
// notifications / quick-create, and a user/admin menu on the right.  Future
// chunks (UI-WORKBENCH-1, UI-DIRECTORY-1, UI-OPERATIONS-1) attach to this
// shell rather than re-implementing the frame each time.
//
// The 6-peer nav target (memo §4) — Home / Workbench / Reports /
// Operations / Analytics / Admin — is built here.  Companies and Contacts
// live as deprecation-styled peers for one cycle until UI-DIRECTORY-1
// removes them and surfaces the Directory entry point.  System Admin's
// nav peer is removed; UI-NAV-1.5 folds its sub-tabs into Admin.  Until
// then, sysadmin-capable users reach SystemAdmin via the "System" tool
// link in the top-right area.
import { useState, useRef, useEffect } from "react";
import { canViewAdminTab, canViewOperations, canViewAccounting, canViewAnalytics, canViewMyInbox, canViewPolicies, getUser, apiFetch } from "../api";
import NotificationBell from "./NotificationBell";
import GlobalNavBar from "./nav/GlobalNavBar";

export default function AppShell({
  user,
  setUser,
  page,
  setPage,
  reviewCount,
  resetSelection,         // () => void — clears selectedClaimId/selectedContactId
  onNavigateToClaim,      // OPS-1: notification → claim
  onNavigateToBillReview, // Scope E: notification → Document Review hub (claimless)
  openTime,               // OPS-1: () => open global Time Entry modal
  openTask,               // OPS-1: () => open global Task modal
  workRefresh,            // OPS-1: bump to refresh the notification bell
  children,
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const createRef = useRef(null);
  // Close the + Create menu on outside click (NOT on mouse-leave, which fired
  // across the small gap between the button and the menu and snapped it shut
  // before a choice could be clicked).
  useEffect(() => {
    if (!createOpen) return;
    const h = (e) => { if (createRef.current && !createRef.current.contains(e.target)) setCreateOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [createOpen]);
  // Visibility gates — capability-driven.  Memo §4: "Operations is visible
  // only to users with operations-related capabilities.  Analytics is
  // visible to users with at least one role-scoped analytics view.
  // Workbench is visible to adjusters by default and to other roles by
  // permission."  For v1, Workbench is visible to every authenticated user.
  const canAdminNav     = user && canViewAdminTab();
  const canOpsNav       = user && canViewOperations();
  const canAccountingNav = user && canViewAccounting();
  const canAnalyticsNav = user && canViewAnalytics();
  const canMyInboxNav   = user && canViewMyInbox();
  const canPoliciesNav  = user && canViewPolicies();

  // Company Policies nav badge = my own open acks + due trainings (mirrors the Admin reviewCount
  // badge). Self-scoped, refreshed on nav change; best-effort so a failure never breaks the shell.
  const [policyOpen, setPolicyOpen] = useState(0);
  useEffect(() => {
    if (!canPoliciesNav) return;
    let live = true;
    apiFetch("/company-policies/my").then((d) => { if (live) setPolicyOpen(d.open_count || 0); })
      .catch(() => {});
    return () => { live = false; };
  }, [canPoliciesNav, page, workRefresh]);

  // Deprecation cycle: Companies + Contacts stay as nav peers but are
  // visually deprioritized (italic + dimmer color + arrow glyph) so the
  // user reads "moving somewhere else."  Removed entirely in UI-DIRECTORY-1.
  // UI-DIRECTORY-1-FOLLOWUP (2026-05-29): retired from nav.  Directory is now
  // the entry point (search + deep-link to the company edit modal + New Company);
  // the review queue lives in Admin; contacts have no standalone create.  The
  // /companies and /contacts routes remain (reached via Directory deep-links).
  const showCompaniesDep = false;
  const showContactsDep  = false;

  // UI-DIRECTORY-1 (2026-05-18): new "Directory" nav peer added between
  // Workbench and the deprecated Companies/Contacts peers.  Directory is
  // the preferred surface; Companies + Contacts stay as deprecated peers
  // for one transition cycle, then drop in UI-DIRECTORY-1-FOLLOWUP.
  const NAV = [
    { id: "dashboard", label: "Home" },
    { id: "planner",   label: "Planner" },
    { id: "claims",    label: "Workbench" },
    ...(canMyInboxNav ? [{ id: "myinbox", label: "My Inbox" }] : []),
    { id: "directory", label: "Directory" },
    ...(showCompaniesDep ? [{ id: "companies", label: "Companies", deprecated: true }] : []),
    ...(showContactsDep  ? [{ id: "contacts",  label: "Contacts",  deprecated: true }] : []),
    { id: "reports",   label: "Reports" },
    ...(canOpsNav        ? [{ id: "operations", label: "Operations" }] : []),
    ...(canAccountingNav ? [{ id: "accounting", label: "Accounting" }] : []),
    ...(canAnalyticsNav ? [{ id: "analytics",  label: "Analytics" }]  : []),
    ...(canPoliciesNav  ? [{ id: "policies",   label: "Company Policies", badge: policyOpen }] : []),
    ...(canAdminNav     ? [{ id: "admin",      label: "Admin", badge: reviewCount }] : []),
  ];

  // Same active-state logic as before: Workbench (id "claims") stays
  // highlighted when on a claim detail page.  Future chunks may add
  // analogous behavior for Workbench preview, Operations sub-routes, etc.
  const isWorkbenchActive = page === "claims" || page === "claim-detail";
  const isProfileActive   = page === "profile";

  // Impersonation + MFA banners (preserved from App.js — Chunks 14E + 17).
  const impersonating = Boolean(typeof localStorage !== "undefined" && localStorage.getItem("original_token"));
  let originalUserName = null;
  if (impersonating) {
    try {
      const ou = JSON.parse(localStorage.getItem("original_user") || "null");
      originalUserName = ou?.full_name || ou?.username || null;
    } catch {}
  }
  const exitImpersonation = () => {
    if (!window.confirm("Return to your own session?")) return;
    const orig = localStorage.getItem("original_token");
    const origUser = localStorage.getItem("original_user");
    const origPerms = localStorage.getItem("original_permissions");
    if (orig) localStorage.setItem("token", orig);
    if (origUser) localStorage.setItem("user", origUser);
    if (origPerms) localStorage.setItem("permissions", origPerms);
    localStorage.removeItem("original_token");
    localStorage.removeItem("original_user");
    localStorage.removeItem("original_permissions");
    window.location.reload();
  };

  // MFA enrollment banner reads from localStorage rather than the user
  // prop so completing enrollment in Profile (which writes localStorage.user)
  // makes the banner disappear without any callback wiring.
  const liveUser = getUser() || user;
  const needsMfaEnrollment = !!(liveUser && liveUser.totp_required && !liveUser.totp_enabled);
  const goToProfile = () => { setPage("profile"); resetSelection(); };
  // UI-NAV-1.5: goToSysadmin removed; sysadmin entry point is Admin → System sub-tabs.

  const onSignOut = () => { localStorage.clear(); setUser(null); };

  const onTabClick = (id) => { setPage(id); resetSelection(); };

  return (
    <>
      {impersonating && (
        <div onClick={exitImpersonation}
             title="Click to return to your own session"
             style={{
               position:"sticky", top:0, zIndex:1000,
               background:"#dc2626", color:"#fff",
               padding:"10px 16px", textAlign:"center",
               fontWeight:600, fontSize:13, letterSpacing:0.3,
               cursor:"pointer", boxShadow:"0 2px 8px rgba(220,38,38,0.4)",
               display:"flex", alignItems:"center", justifyContent:"center", gap:10,
             }}>
          <span style={{fontSize:14}}>🔒</span>
          <span>VIEWING AS <strong style={{fontSize:14}}>{user.full_name || user.username}</strong>{originalUserName ? ` — impersonated by ${originalUserName}` : ""}</span>
          <span style={{fontWeight:400, opacity:0.9}}>· click to return to your own session</span>
        </div>
      )}
      {needsMfaEnrollment && (
        <div onClick={goToProfile}
             title="Click to set up MFA in your Profile"
             style={{
               position:"sticky", top:0, zIndex:999,
               background:"#f59e0b", color:"#000",
               padding:"10px 16px", textAlign:"center",
               fontWeight:600, fontSize:13, letterSpacing:0.3,
               cursor:"pointer", boxShadow:"0 2px 8px rgba(245,158,11,0.4)",
               display:"flex", alignItems:"center", justifyContent:"center", gap:10,
             }}>
          <span style={{fontSize:14}}>⚠️</span>
          <span>MFA REQUIRED — your admin has required two-factor authentication on your account.</span>
          <span style={{fontWeight:400, opacity:0.9}}>· click to enroll in your Profile</span>
        </div>
      )}
      <div className="app">
        <nav className="topnav">
          <div className="topnav-brand">WALTERS <span>NORTHWEST</span></div>
          <div className="nav-tabs">
            {NAV.map(n => {
              const active = (n.id === "claims" ? isWorkbenchActive : page === n.id);
              const className = `nav-tab ${active ? "active" : ""} ${n.deprecated ? "deprecated" : ""}`.trim();
              return (
                <button key={n.id}
                  className={className}
                  title={n.deprecated ? "Moving to Directory in a future update" : undefined}
                  onClick={() => onTabClick(n.id)}>
                  {n.label}
                  {n.badge > 0 && (
                    <span style={{marginLeft:6,padding:"1px 7px",borderRadius:10,background:"#f59e0b",color:"#fff",fontSize:10,fontWeight:700,verticalAlign:"middle"}}>
                      {n.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="topnav-right">
            {/* OPS-1 (2026-05-29): global Quick-Create + notification bell.
                Time Entry is the primary action (mission-critical); Task is
                the secondary. Available on every page. */}
            <div ref={createRef} style={{ position: "relative" }}>
              <button className="btn btn-blue" style={{ padding: "6px 12px", fontSize: 13, fontWeight: 600 }}
                      onClick={() => setCreateOpen(o => !o)}>+ Create ▾</button>
              {createOpen && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", zIndex: 1200, minWidth: 180, overflow: "hidden" }}>
                  <button style={createItem} onClick={() => { setCreateOpen(false); openTime && openTime({}); }}>🕑 &nbsp;Time Entry</button>
                  <button style={createItem} onClick={() => { setCreateOpen(false); openTask && openTask({}); }}>✓ &nbsp;Task</button>
                </div>
              )}
            </div>
            <button title="Log time"
                    style={{ padding: "6px 12px", fontSize: 13, fontWeight: 600, color: "#fff",
                             background: "transparent", border: "1px solid rgba(255,255,255,0.45)",
                             borderRadius: 4, cursor: "pointer" }}
                    onClick={() => openTime && openTime({})}>+ Time</button>
            <NotificationBell onNavigateToClaim={onNavigateToClaim} onNavigateToBillReview={onNavigateToBillReview} refreshKey={workRefresh} />
            <div className="user-badge" onClick={goToProfile}
                 title="View profile"
                 style={{ cursor: "pointer", padding: "4px 8px", borderRadius: 4, background: isProfileActive ? "rgba(255,255,255,0.15)" : "transparent", transition: "background 0.15s" }}>
              <div className="user-avatar">{user.full_name?.charAt(0) || "U"}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 12 }}>{user.full_name}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "capitalize" }}>{(user.role || "").replace(/_/g, " ")}</div>
              </div>
            </div>
            <button className="btn-logout" onClick={onSignOut}>Sign Out</button>
          </div>
        </nav>
        <GlobalNavBar />
        <main className="main">
          {children}
        </main>
      </div>
    </>
  );
}

const createItem = {
  display: "block", width: "100%", textAlign: "left", padding: "10px 14px",
  background: "#fff", border: "none", borderBottom: "1px solid var(--border-light, #f0f0f0)",
  cursor: "pointer", fontSize: 13, color: "var(--text1, #1a2438)", fontWeight: 500,
};
